/**
 * 应用外壳：受保护路由容器（RequireAuth）+ 侧边导航 + 顶栏。
 * 顶栏集成：快速添加、通知铃铛、主题切换、锁定保险库、登出；移动端汉堡菜单。
 */
import { useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import { useTheme } from '../stores/theme';
import { useVault } from '../stores/vault';
import { Icon, type IconName } from './icons';
import { NotificationBell } from './NotificationBell';
import { QuickAdd } from './QuickAdd';
import { Button, ConfirmDialog, Spinner } from './ui';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  group: string;
}

const NAV: NavItem[] = [
  { to: '/', label: '仪表盘', icon: 'dashboard', group: '工作台' },
  { to: '/tasks', label: '任务', icon: 'tasks', group: '工作台' },
  { to: '/projects', label: '项目', icon: 'projects', group: '工作台' },
  { to: '/habits', label: '习惯', icon: 'habits', group: '工作台' },
  { to: '/focus', label: '专注', icon: 'focus', group: '工作台' },
  { to: '/stats', label: '统计', icon: 'stats', group: '工作台' },
  { to: '/contacts', label: '人脉', icon: 'contacts', group: '人脉' },
  { to: '/vault', label: '帐号管理', icon: 'vault', group: '安全' },
  { to: '/settings', label: '设置', icon: 'settings', group: '安全' },
];

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const groups = Array.from(new Set(NAV.map((n) => n.group)));
  return (
    <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
      {groups.map((g) => (
        <div key={g}>
          <div className="mb-1.5 px-3 text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">{g}</div>
          <div className="space-y-0.5">
            {NAV.filter((n) => n.group === g).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
                  }`
                }
              >
                <Icon name={n.icon} className="h-5 w-5" />
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const { isDark, toggle } = useTheme();
  const vaultUnlocked = useVault((s) => s.unlocked);
  const lockVault = useVault((s) => s.lock);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [busy, setBusy] = useState(false);

  // 路由变化时收起移动端菜单
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  async function doLogout() {
    setBusy(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } finally {
      setBusy(false);
      setConfirmLogout(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      {/* 桌面侧边栏 */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 lg:flex">
        <div className="flex h-14 items-center gap-2 border-b border-slate-200 px-4 dark:border-slate-800">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <Icon name="sun" className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold">追光宝盒</div>
            <div className="text-xs text-slate-400">Light Chaser</div>
          </div>
        </div>
        <NavList />
        <div className="border-t border-slate-200 p-3 dark:border-slate-800">
          <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-sm font-medium text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
              {(user?.displayName ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{user?.displayName ?? user?.email}</div>
              <div className="truncate text-xs text-slate-400">{user?.email}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* 移动端遮罩 */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)}>
          <aside
            className="absolute inset-y-0 left-0 flex w-64 flex-col bg-white shadow-xl dark:bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-14 items-center justify-between border-b border-slate-200 px-4 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
                  <Icon name="sun" className="h-5 w-5" />
                </div>
                <span className="text-sm font-semibold">追光宝盒</span>
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label="关闭菜单"
              >
                <Icon name="close" className="h-5 w-5" />
              </button>
            </div>
            <NavList onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* 顶栏 */}
      <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-slate-200 bg-white/90 px-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/90 lg:pl-64">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 lg:hidden"
          aria-label="打开菜单"
        >
          <Icon name="menu" className="h-5 w-5" />
        </button>
        <div className="flex-1" />
        <Button size="sm" onClick={() => setQuickOpen(true)}>
          <span className="flex items-center gap-1">
            <Icon name="plus" className="h-4 w-4" />
            <span className="hidden sm:inline">添加任务</span>
          </span>
        </Button>
        <NotificationBell />
        <button
          type="button"
          onClick={toggle}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          aria-label={isDark ? '切换到浅色' : '切换到深色'}
          title={isDark ? '切换到浅色' : '切换到深色'}
        >
          <Icon name={isDark ? 'sun' : 'moon'} className="h-5 w-5" />
        </button>
        {vaultUnlocked && (
          <button
            type="button"
            onClick={lockVault}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label="锁定保险库"
            title="锁定保险库"
          >
            <Icon name="lock" className="h-5 w-5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setConfirmLogout(true)}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          aria-label="退出登录"
          title="退出登录"
        >
          <Icon name="logout" className="h-5 w-5" />
        </button>
      </header>

      {/* 主内容 */}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-4 py-6">
          <Outlet />
        </div>
      </main>

      <QuickAdd open={quickOpen} onClose={() => setQuickOpen(false)} />
      <ConfirmDialog
        open={confirmLogout}
        title="退出登录"
        message="退出后需要重新登录，保险库将被锁定。确定退出吗？"
        confirmText="退出"
        busy={busy}
        onConfirm={doLogout}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
}

/** 受保护路由：未登录跳 /login；会话加载中显示整页加载态 */
export function RequireAuth() {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Spinner className="h-8 w-8 text-indigo-600" />
      </div>
    );
  }
  if (status === 'guest') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <AppShell />;
}
